import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import axios from 'axios'
import style from '../pages/CensusManagement.module.scss'
import { Input, Layout as AdLayout } from '@surecompanies/core_components'
import Page from 'layouts/Page'
import PageWrapper from '../../../PageWrapper'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import FileInfo from '../components/FileInfo'
import ReviewTabs from '../components/ReviewTabs'
import GridWrapper from '../components/GridWrapper'
import { CensusFileDto } from '../api/dto/census-file.dto'
import { SimpleCustomerDto } from '../../../api/persons/dto/simple-customer.dto'
import { getMyClients } from '../../../api/persons'
import {
  CENSUS_MANAGEMENT_PATH,
  DEFAULT_ROWS_PAGINATION_LIMIT,
} from '../constants'
import {
  CellValueChangedEvent,
  ColDef,
  GridReadyEvent,
  IServerSideGetRowsParams,
  SelectionChangedEvent,
} from '@ag-grid-community/core'
import { CensusFileRowTypeEnum } from '../api/enums/census-file-row-type.enum'
import { CensusFileRowGridData } from '../types/census-file-row-grid-data'
import { colDefsFromData } from '../utils/col-defs-from-data'
import { CensusFileRowStatusEnum } from '../api/enums/census-file-row-status.enum'
import { SortDirection } from 'enrollment-platform/types/sorted-request.dto'
import { CensusFileRowCountsDto } from '../api/dto/census-file-row-counts.dto'
import { getInProcessPageData } from '../utils/get-in-process-page-data'

const { Content } = AdLayout

interface ActionButtonProps {
  censusRows: CensusFileRowGridData[]
  selectedRows: CensusFileRowGridData[]
  handleSubmit: () => void
  counts: CensusFileRowCountsDto | undefined
}

interface ReviewRecordsBaseProps {
  showReviewTabs?: boolean
  tabKey: string
  routeParams: { importPublicKey: string }
  actionButton?: (props: ActionButtonProps) => ReactElement<any>
  rowQueryParams: {
    censusRowType?: string
    censusFileRowStatuses: CensusFileRowStatusEnum[]
  }
  handleCellValueChanged?: (
    cellValueChangedEvent: CellValueChangedEvent
  ) => void
  // function called by ReviewRecordsBase to customize base column definitions
  modifyColDefs?: (
    baseColDefs: ColDef<CensusFileRowGridData>[]
  ) => ColDef<CensusFileRowGridData>[]
  onSelectionChangedCallback?: (
    selected: CensusFileRowGridData[],
    source: SelectionChangedEvent<CensusFileRowGridData>['source']
  ) => void
  onSelectAllCallback?: (total: number) => void
  pageTitle?: string
  showSearch?: boolean
  onSearchChange?: (search: string) => void
}

// If sorting  by a column name that has data in 'rowData', we have to apply a prefix to the field name so that the backend recognizes that the
// field is in 'rowData'
const getSortParams = (
  params: IServerSideGetRowsParams<CensusFileRowGridData>
): [string, SortDirection] => {
  const sortModel = params.request.sortModel

  const { colId, sort: direction } = sortModel[0] // Take the first sort entry
  const colDef = params.api.getColumnDef(colId)
  const prefix = colDef?.['sortPrefix'] ?? ''

  const sort = `${prefix}${colId}`
  const order =
    direction.toUpperCase() === 'DESC' ? SortDirection.desc : SortDirection.asc
  return [sort, order]
}

function ReviewRecordsBase({
  routeParams,
  showReviewTabs,
  actionButton,
  rowQueryParams,
  handleCellValueChanged,
  modifyColDefs,
  onSelectionChangedCallback,
  onSelectAllCallback,
  tabKey,
  pageTitle = 'Review Records',
  showSearch = false,
  onSearchChange,
}: ReviewRecordsBaseProps) {
  const { censusRowType: paramCensusRowType, censusFileRowStatuses } =
    rowQueryParams
  const [counts, setCounts] = useState<CensusFileRowCountsDto | undefined>()
  const [censusFile, setCensusFile] = useState<CensusFileDto | undefined>()
  const [contributionGroups, setContributionGroups] = useState<string[]>([])
  const { importPublicKey: publicKey } = routeParams
  const [censusRowType, setCensusRowType] = useState<string | undefined>(
    paramCensusRowType
  )
  const [selectedRows, setSelectedRows] = useState<CensusFileRowGridData[]>([])
  const [censusRows, setCensusRows] = useState<CensusFileRowGridData[]>([])
  const searchTermRef = useRef('')
  const cachedFilteredRowsRef = useRef<CensusFileRowGridData[] | null>(null)
  const lastSortModelRef = useRef<string>('')
  const gridRef = useRef<GridReadyEvent | undefined>(undefined)
  const totalRowsRef = useRef(0)
  const [clients, setClients] = useState<SimpleCustomerDto[]>([])
  // State to store dynamically generated column definitions
  const [dynamicColDefs, setDynamicColDefs] = useState<
    ColDef<CensusFileRowGridData>[]
  >([])

  // Get client name helper
  const clientName = (key) => clients.find((c) => c.id === key)?.name ?? '...'

  const resetGrid = useCallback(() => {
    cachedFilteredRowsRef.current = null
    gridRef.current?.api.refreshServerSide({ purge: true })
    gridRef.current?.api.deselectAll()
    setSelectedRows([])
  }, [])

  const onGridReady = useCallback((params: GridReadyEvent) => {
    gridRef.current = params
  }, [])

  const onTabChange = useCallback((tab: string) => {
    setCensusRowType(tab)
    setSelectedRows([])
    searchTermRef.current = ''
    cachedFilteredRowsRef.current = null
    lastSortModelRef.current = ''
    gridRef.current?.api.refreshServerSide({ purge: true })
  }, [])

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.api.updateGridOptions({
        serverSideDatasource: { getRows },
      })
    }
  }, [gridRef.current])

  const buildColDefs = (mergedData: CensusFileRowGridData[]) => {
    let newColDefs = colDefsFromData(mergedData[0].rowData)
    if (censusRowType === CensusFileRowTypeEnum.ERROR) {
      const errorKeys = new Set<string>()
      mergedData.forEach((row) => {
        if (row.errors) {
          Object.keys(row.errors).forEach((errorKey) => errorKeys.add(errorKey))
        }
      })
      newColDefs = [
        ...newColDefs.filter((col) => errorKeys.has(col.field ?? '')),
        ...newColDefs.filter((col) => !errorKeys.has(col.field ?? '')),
      ]
    }
    return newColDefs
  }

  // Function to fetch and set rows, also dynamically generates `colDefs`
  const getRows = async (
    params: IServerSideGetRowsParams<CensusFileRowGridData>
  ) => {
    const limit = DEFAULT_ROWS_PAGINATION_LIMIT
    let sort: string | undefined
    let order: SortDirection | undefined
    try {
      const { startRow, sortModel } = params.request

      if (sortModel.length > 0) {
        ;[sort, order] = getSortParams(params)
      }

      const currentSearch = searchTermRef.current

      if (currentSearch) {
        const currentSortKey = JSON.stringify(params.request.sortModel)
        if (
          !cachedFilteredRowsRef.current ||
          lastSortModelRef.current !== currentSortKey
        ) {
          lastSortModelRef.current = currentSortKey
          const {
            rowCounts,
            data,
            censusFile: cf,
            contributionGroups: cg,
          } = await getInProcessPageData({
            limit: totalRowsRef.current || DEFAULT_ROWS_PAGINATION_LIMIT,
            page: 1,
            publicKey,
            sort,
            order,
            censusRowType,
            censusFileRowStatuses,
            search: currentSearch,
          })
          setCounts(rowCounts)
          setCensusFile(cf)
          setContributionGroups(cg ?? [])
          const allMerged: CensusFileRowGridData[] = data.map((d) => ({
            ...d,
            ...d.rowData,
          }))
          if (allMerged.length > 0 && dynamicColDefs.length === 0) {
            setDynamicColDefs(buildColDefs(allMerged))
          }
          cachedFilteredRowsRef.current = allMerged
        }

        const filtered = cachedFilteredRowsRef.current
        const pageStart = startRow ?? 0
        const slice = filtered.slice(pageStart, pageStart + limit)
        setCensusRows(slice)
        params.success({ rowData: slice, rowCount: filtered.length })
        return
      }

      // Normal server-side pagination
      cachedFilteredRowsRef.current = null
      const page = Math.floor((startRow ?? 1) / limit) + 1
      const { rowCounts, data, pagination, censusFile, contributionGroups } =
        await getInProcessPageData({
          limit,
          page,
          publicKey,
          sort,
          order,
          censusRowType,
          censusFileRowStatuses,
        })

      setCounts(rowCounts)
      setCensusFile(censusFile)
      setContributionGroups(contributionGroups ?? [])
      const mergedData: CensusFileRowGridData[] = data.map((d) => ({
        ...d,
        ...d.rowData,
      }))
      setCensusRows(mergedData)

      if (mergedData.length > 0 && dynamicColDefs.length === 0) {
        setDynamicColDefs(buildColDefs(mergedData))
      }

      totalRowsRef.current = pagination.total ?? 0
      params.success({
        rowData: mergedData,
        rowCount: pagination.total,
      })
    } catch (error) {
      if (axios.isCancel(error)) return
      console.error('Error fetching data:', error)
      params.fail()
    }
  }

  const onCellValueChanged = useCallback(
    (params: CellValueChangedEvent<CensusFileRowGridData>) => {
      handleCellValueChanged && handleCellValueChanged(params)
    },
    []
  )

  const fetchClients = async () => {
    const data = await getMyClients()
    setClients(data.sort((a, b) => a.name.localeCompare(b.name)))
  }
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchClients()
    }, 1)
    return () => clearTimeout(timer)
  }, [])

  const handleSearch = (value: string) => {
    const term = value.trim()
    searchTermRef.current = term
    cachedFilteredRowsRef.current = null
    lastSortModelRef.current = ''
    setSelectedRows([])
    gridRef.current?.api.deselectAll()
    gridRef.current?.api.refreshServerSide({ purge: true })
    onSearchChange?.(term)
  }

  // Apply `modifyColDefs` function if provided
  const finalColDefs = modifyColDefs
    ? modifyColDefs(dynamicColDefs)
    : dynamicColDefs

  // keep track of rows selected in grid
  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent<CensusFileRowGridData>) => {
      setTimeout(() => {
        const selectedRows: CensusFileRowGridData[] = []

        event.api.forEachNode((node) => {
          if (node.isSelected() && node.data) {
            selectedRows.push(node.data)
          }
        })

        setSelectedRows(selectedRows)
        onSelectionChangedCallback?.(selectedRows, event.source)

        if (
          (event.source === 'uiSelectAll' ||
            event.source === 'uiSelectAllCurrentPage') &&
          selectedRows.length > 0
        ) {
          onSelectAllCallback?.(totalRowsRef.current)
        }
      }, 0)
    },
    [onSelectionChangedCallback, onSelectAllCallback]
  )

  return (
    <PageWrapper>
      <Page
        title={pageTitle}
        action={
          actionButton &&
          actionButton({
            censusRows,
            selectedRows,
            handleSubmit: resetGrid,
            counts,
          })
        }
        breadcrumb={
          <BreadcrumbNav
            items={[
              { title: 'Census Management', path: CENSUS_MANAGEMENT_PATH },
              { title: 'Review Records' },
            ]}
          />
        }
      >
        <AdLayout>
          <Content className={style.content}>
            <FileInfo
              data={censusFile}
              clientName={clientName(censusFile?.clientPublicKey)}
              style={{ padding: '15px 0 0 15px' }}
            />
            <ReviewTabs
              visible={showReviewTabs}
              publicKey={publicKey}
              counts={counts}
              onTabChange={onTabChange}
              currentTab={tabKey}
              style={{ padding: '15px 0 0 15px' }}
            />
            {/* Search field */}
            {showSearch && (
              <div style={{ padding: '8px 15px' }}>
                <Input.Search
                  placeholder="Search by first or last name"
                  allowClear
                  onSearch={handleSearch}
                  style={{ width: 280 }}
                />
              </div>
            )}
            <GridWrapper
              onSelectionChanged={onSelectionChanged}
              columnDefs={finalColDefs}
              onGridReady={onGridReady}
              rowSelection="multiple"
              onCellValueChanged={onCellValueChanged}
              additionalProps={{ context: { contributionGroups } }}
            />
          </Content>
        </AdLayout>
      </Page>
    </PageWrapper>
  )
}

export default ReviewRecordsBase
